/**
 * CreativeGrid component for displaying creatives with filtering and sorting
 * Combines FilterBar and CreativeCard with client-side filtering logic
 */

import { useQueryClient } from '@tanstack/react-query';
import { Video, Trash2, MoreVertical } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ConfirmDeleteModal } from '@/components/ui/ConfirmDeleteModal';
import { useToast } from '@/components/ui/Toast';
import { analysesKeys } from '@/hooks/useAnalysis';
import { useDeleteCreative } from '@/hooks/useCreatives';
import { useCreativeViewState, DEFAULT_FILTERS } from '@/hooks/useCreativeViewState';
import { cn } from '@/lib/utils';
import { api } from '@/services/api';
import type { Creative, Analysis, AnalysisDecision, MediaType } from '@/types';
import type { TaxonomySchema, TaxonomyExtraction } from '@/types/taxonomy';

import { CreativeCard, CreativeCardSkeleton } from './CreativeCard';
import {
  FilterBar,
  type FilterState,
  type SortField,
  type SortOrder,
} from './FilterBar';

// ============================================================================
// TYPES
// ============================================================================

// Extended Creative type with taxonomy data
export interface CreativeWithTaxonomy extends Creative {
  taxonomyExtraction?: TaxonomyExtraction | null;
}

export interface CreativeGridProps {
  creatives: CreativeWithTaxonomy[];
  isLoading?: boolean;
  projectId: string;
  taxonomySchema?: TaxonomySchema | null;
}

// ============================================================================
// FILTERING AND SORTING LOGIC
// ============================================================================

function filterCreatives(
  creatives: CreativeWithTaxonomy[],
  filters: FilterState
): CreativeWithTaxonomy[] {
  return creatives.filter((creative) => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const nameMatch = creative.name.toLowerCase().includes(searchLower);
      const productMatch = creative.metadata?.product?.toLowerCase().includes(searchLower);
      if (!nameMatch && !productMatch) {
        return false;
      }
    }

    // Media type filter
    if (filters.mediaType !== 'all') {
      const creativeMediaType = creative.mediaType || 'video'; // backward compat
      if (filters.mediaType === 'video') {
        if (creativeMediaType !== 'video') return false;
      } else if (filters.mediaType === 'image') {
        if (creativeMediaType !== 'image') return false;
      }
    }

    // Decision filter
    if (filters.decision !== 'all') {
      if (filters.decision === 'pending') {
        if (creative.latestDecision) {
          return false;
        }
      } else if (creative.latestDecision !== filters.decision) {
        return false;
      }
    }

    // Objective filter
    if (filters.objective !== 'all') {
      if (creative.metadata?.objective !== filters.objective) {
        return false;
      }
    }

    // Placement filter
    if (filters.placement !== 'all') {
      if (creative.metadata?.placement !== filters.placement) {
        return false;
      }
    }

    // Taxonomy filters
    if (filters.taxonomyFilters && filters.taxonomyFilters.length > 0) {
      const extraction = creative.taxonomyExtraction;
      if (!extraction || !extraction.values || extraction.values.length === 0) {
        return false;
      }

      // All taxonomy filters must match
      for (const filter of filters.taxonomyFilters) {
        const matchingValue = extraction.values.find(
          (v) => v.fieldId === filter.fieldId && v.optionId === filter.optionId
        );
        if (!matchingValue) {
          return false;
        }
      }
    }

    return true;
  });
}

function sortCreatives(
  creatives: CreativeWithTaxonomy[],
  sortBy: SortField,
  sortOrder: SortOrder
): CreativeWithTaxonomy[] {
  const sorted = [...creatives].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'createdAt':
        comparison =
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'updatedAt':
        comparison =
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
        break;
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'decision': {
        // Sort order: Scale > Fix > Kill > null
        const decisionOrder: Record<AnalysisDecision | 'pending', number> = {
          Scale: 0,
          Fix: 1,
          Kill: 2,
          pending: 3,
        };
        const aOrder = a.latestDecision
          ? decisionOrder[a.latestDecision]
          : decisionOrder.pending;
        const bOrder = b.latestDecision
          ? decisionOrder[b.latestDecision]
          : decisionOrder.pending;
        comparison = aOrder - bOrder;
        break;
      }
    }

    return sortOrder === 'asc' ? comparison : -comparison;
  });

  return sorted;
}

// ============================================================================
// EMPTY STATE COMPONENT
// ============================================================================

interface EmptyStateProps {
  hasFilters: boolean;
  onClearFilters: () => void;
  projectId: string;
}

function EmptyState({ hasFilters, onClearFilters, projectId }: EmptyStateProps) {
  const navigate = useNavigate();

  if (hasFilters) {
    return (
      <Card className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-subtle mb-4">
          <Video className="h-8 w-8 text-muted" />
        </div>
        <h3 className="text-lg font-semibold text-primary mb-2">
          No matching creatives
        </h3>
        <p className="text-sm text-secondary mb-6 max-w-sm mx-auto">
          Try adjusting your filters or search criteria to find what you&apos;re
          looking for.
        </p>
        <Button variant="secondary" onClick={onClearFilters}>
          Clear Filters
        </Button>
      </Card>
    );
  }

  return (
    <Card className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-subtle mb-4">
        <Video className="h-8 w-8 text-muted" />
      </div>
      <h3 className="text-lg font-semibold text-primary mb-2">
        No creatives yet
      </h3>
      <p className="text-sm text-secondary mb-6 max-w-sm mx-auto">
        Upload your first video creative to start analyzing and making
        data-driven decisions.
      </p>
      <Button onClick={() => navigate(`/projects/${projectId}/upload`)}>
        Upload Creative
      </Button>
    </Card>
  );
}

// ============================================================================
// CREATIVE GRID COMPONENT
// ============================================================================

/** Get effective media type for a creative (defaults to 'video' for backward compat) */
function getCreativeMediaType(creative: Creative): MediaType {
  if (creative.mediaType) return creative.mediaType;
  const imageFormats = ['jpg', 'jpeg', 'png', 'webp'];
  if (imageFormats.includes(creative.format?.toLowerCase() || '')) return 'image';
  return 'video';
}

export function CreativeGrid({
  creatives,
  isLoading = false,
  projectId,
  taxonomySchema,
}: CreativeGridProps) {
  const navigate = useNavigate();
  const { warning: showWarning } = useToast();

  // Filter and view state (persisted to localStorage per-project)
  const [{ filters, viewMode }, setViewState] = useCreativeViewState(projectId);

  // Transient UI state (not persisted)
  const [isCompareMode, setIsCompareMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

  // Apply filtering and sorting
  const filteredCreatives = React.useMemo(() => {
    const filtered = filterCreatives(creatives, filters);
    return sortCreatives(filtered, filters.sortBy, filters.sortOrder);
  }, [creatives, filters]);

  // Determine the media type of the first selected creative (for same-type enforcement)
  const selectedMediaType = React.useMemo((): MediaType | null => {
    if (selectedIds.size === 0) return null;
    const firstSelectedId = selectedIds.values().next().value;
    const firstSelected = creatives.find((c) => c.creativeId === firstSelectedId);
    return firstSelected ? getCreativeMediaType(firstSelected) : null;
  }, [selectedIds, creatives]);

  // Compare mode toggle
  const handleToggleCompareMode = () => {
    setIsCompareMode((prev) => {
      if (prev) {
        // Exiting compare mode - clear selection
        setSelectedIds(new Set());
      }
      return !prev;
    });
  };

  // Selection handlers - enforce same media type
  const handleSelect = (creativeId: string, selected: boolean) => {
    if (selected && selectedIds.size > 0 && selectedMediaType) {
      // Enforce same-type comparison
      const creative = creatives.find((c) => c.creativeId === creativeId);
      if (creative) {
        const creativeType = getCreativeMediaType(creative);
        if (creativeType !== selectedMediaType) {
          showWarning(
            'Same type required',
            'Only creatives of the same media type can be compared'
          );
          return;
        }
      }
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(creativeId);
      } else {
        next.delete(creativeId);
      }
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleCompareSelected = () => {
    const ids = Array.from(selectedIds).join(',');
    navigate(`/compare?ids=${ids}`);
  };

  const handleClearFilters = () => {
    setViewState({ filters: DEFAULT_FILTERS });
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.search.length > 0 ||
    filters.decision !== 'all' ||
    filters.mediaType !== 'all' ||
    filters.objective !== 'all' ||
    filters.placement !== 'all' ||
    (filters.taxonomyFilters?.length ?? 0) > 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <FilterBar
          filters={filters}
          onFilterChange={(f) => setViewState({ filters: f })}
          viewMode={viewMode}
          onViewModeChange={(m) => setViewState({ viewMode: m })}
          taxonomySchema={taxonomySchema}
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <CreativeCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (creatives.length === 0 || filteredCreatives.length === 0) {
    return (
      <div className="space-y-6">
        {creatives.length > 0 && (
          <FilterBar
            filters={filters}
            onFilterChange={(f) => setViewState({ filters: f })}
            viewMode={viewMode}
            onViewModeChange={(m) => setViewState({ viewMode: m })}
            isCompareMode={isCompareMode}
            onToggleCompareMode={handleToggleCompareMode}
            selectedCount={selectedIds.size}
            onClearSelection={handleClearSelection}
            onCompareSelected={handleCompareSelected}
            taxonomySchema={taxonomySchema}
          />
        )}
        <EmptyState
          hasFilters={hasActiveFilters && creatives.length > 0}
          onClearFilters={handleClearFilters}
          projectId={projectId}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter bar */}
      <FilterBar
        filters={filters}
        onFilterChange={(f) => setViewState({ filters: f })}
        viewMode={viewMode}
        onViewModeChange={(m) => setViewState({ viewMode: m })}
        isCompareMode={isCompareMode}
        onToggleCompareMode={handleToggleCompareMode}
        selectedCount={selectedIds.size}
        onClearSelection={handleClearSelection}
        onCompareSelected={handleCompareSelected}
        taxonomySchema={taxonomySchema}
      />

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-secondary">
          Showing {filteredCreatives.length} of {creatives.length} creative
          {creatives.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Grid/List view */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredCreatives.map((creative) => {
            const isDisabledForCompare = isCompareMode && selectedMediaType !== null &&
              getCreativeMediaType(creative) !== selectedMediaType &&
              !selectedIds.has(creative.creativeId);
            return (
              <CreativeCard
                key={creative.creativeId}
                creative={creative}
                showCheckbox={isCompareMode}
                isSelected={selectedIds.has(creative.creativeId)}
                isCompareDisabled={isDisabledForCompare}
                onSelect={handleSelect}
                showActions={!isCompareMode}
              />
            );
          })}
        </div>
      ) : (
        <div className="border border-border-subtle rounded-xl overflow-hidden bg-surface">
          {/* Table Header */}
          <div className="grid grid-cols-[auto_minmax(200px,2fr)_80px_100px_100px_90px_90px_44px] gap-4 px-4 py-3 bg-surface-subtle border-b border-border-subtle text-xs font-medium text-secondary uppercase tracking-wide">
            {isCompareMode && <div className="w-5" />}
            <div className="col-span-2">Creative</div>
            <div>Duration</div>
            <div>Objective</div>
            <div>Placement</div>
            <div>Created</div>
            <div>Decision</div>
            <div />
          </div>
          {/* Table Body */}
          <div className="divide-y divide-border-subtle">
            {filteredCreatives.map((creative) => (
              <CreativeListItem
                key={creative.creativeId}
                creative={creative}
                showCheckbox={isCompareMode}
                isSelected={selectedIds.has(creative.creativeId)}
                onSelect={handleSelect}
                showActions={!isCompareMode}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LIST VIEW ITEM COMPONENT
// ============================================================================

interface CreativeListItemProps {
  creative: Creative;
  showCheckbox?: boolean;
  isSelected: boolean;
  onSelect: (creativeId: string, selected: boolean) => void;
  showActions?: boolean;
}

function CreativeListItem({
  creative,
  showCheckbox = false,
  isSelected,
  onSelect,
  showActions = true,
}: CreativeListItemProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const deleteCreativeMutation = useDeleteCreative(creative.creativeId);

  const handlePrefetchAnalyses = React.useCallback(() => {
    queryClient.prefetchQuery({
      queryKey: analysesKeys.list({ creativeId: creative.creativeId }),
      queryFn: () => api.get<Analysis[]>(`/creatives/${creative.creativeId}/analyses`),
      staleTime: 2 * 60 * 1000,
    });
  }, [queryClient, creative.creativeId]);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const decisionStyles: Record<AnalysisDecision, string> = {
    Scale: 'bg-success-soft text-success',
    Fix: 'bg-warning-soft text-warning',
    Kill: 'bg-danger-soft text-danger',
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteCreativeMutation.mutateAsync();
      setShowDeleteModal(false);
    } catch {
      // Error already logged by onError handler, close modal anyway
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <div
        className={cn(
          'grid grid-cols-[auto_minmax(200px,2fr)_80px_100px_100px_90px_90px_44px] gap-4 px-4 py-3 items-center cursor-pointer transition-colors',
          'hover:bg-surface-subtle',
          isSelected && 'bg-brand/5'
        )}
        onClick={() => navigate(`/creatives/${creative.creativeId}`)}
        onMouseEnter={handlePrefetchAnalyses}
      >
        {/* Checkbox - only shown in compare mode */}
        {showCheckbox && (
          <label
            className="flex items-center justify-center w-5 h-5 rounded border border-border-subtle cursor-pointer hover:border-brand"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(creative.creativeId, e.target.checked)}
              className="sr-only"
            />
            {isSelected && (
              <svg
                className="h-3 w-3 text-brand"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </label>
        )}

        {/* Thumbnail + Name */}
        <div className="flex items-center gap-3 min-w-0 col-span-2">
          <div className="w-14 h-9 rounded-md overflow-hidden bg-surface-subtle flex-shrink-0">
            {creative.thumbnailUrl ? (
              <img
                src={creative.thumbnailUrl}
                alt={creative.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Video className="h-4 w-4 text-muted" />
              </div>
            )}
          </div>
          <span className="text-sm font-medium text-primary truncate">
            {creative.name}
          </span>
        </div>

        {/* Duration */}
        <span className="text-sm text-secondary tabular-nums">
          {formatDuration(creative.duration)}
        </span>

        {/* Objective */}
        <span className="text-sm text-secondary truncate">
          {creative.metadata?.objective || '—'}
        </span>

        {/* Placement */}
        <span className="text-sm text-secondary truncate">
          {creative.metadata?.placement || '—'}
        </span>

        {/* Created Date */}
        <span className="text-sm text-muted">
          {formatDate(creative.createdAt)}
        </span>

        {/* Decision badge */}
        <div>
          {creative.latestDecision ? (
            <span
              className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold uppercase ${decisionStyles[creative.latestDecision]}`}
            >
              {creative.latestDecision}
            </span>
          ) : creative.status === 'processing' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-info-soft text-info">
              <span className="w-1.5 h-1.5 rounded-full bg-info animate-pulse" />
              Analyzing
            </span>
          ) : (
            <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-surface-subtle text-muted">
              Pending
            </span>
          )}
        </div>

        {/* Action menu */}
        {showActions && !showCheckbox ? (
          <div ref={menuRef} className="relative flex justify-end">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className={cn(
                'p-1.5 rounded-md transition-colors',
                'text-muted hover:text-primary hover:bg-surface-subtle',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50'
              )}
              aria-label="More actions"
            >
              <MoreVertical className="h-4 w-4" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 z-10 w-36 bg-surface rounded-lg shadow-lg border border-border-subtle py-1">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowMenu(false);
                    setShowDeleteModal(true);
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
                    'text-danger hover:bg-danger-soft transition-colors'
                  )}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              </div>
            )}
          </div>
        ) : (
          <div />
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Creative"
        description="This action cannot be undone. The creative and all associated analysis data will be permanently deleted."
        itemName={creative.name}
        confirmText="Delete Creative"
        isLoading={deleteCreativeMutation.isPending}
      />
    </>
  );
}
